import type { ChangeEvent } from 'react';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';

import toastMsg from '../../../../../lib/toastMsg';
import { useSlideImageUploadMutate } from '../hooks/useSlideImageUploadMutate';
import { UploadSlotStyle } from './SlideImageUpload.style';
import { SlideImage } from '../types';
import { SLIDE_IMAGE_ALLOWED_MIME_TYPES, SLIDE_IMAGE_INVALID_TYPE_MESSAGE } from '../constants';

type SlideImageUploadProps = {
  value?: SlideImage | null;
  onChange?: (next: SlideImage | null) => void;
};

type SlideImageUploadRef = Pick<SlideImageUploadProps, 'value' | 'onChange'>;

export const SlideImageUpload = forwardRef<SlideImageUploadRef, SlideImageUploadProps>(
  (props, ref) => {
    const { value = null, onChange } = props;
    const fileInputRef = useRef<HTMLInputElement>(null);

    // antd compatible Form.Item 호환 어댑터 (어드민 공통 컨벤션)
    useImperativeHandle(ref, () => ({ value, onChange }));

    const { mutate: uploadImage, isLoading } = useSlideImageUploadMutate(onChange);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;

      if (!SLIDE_IMAGE_ALLOWED_MIME_TYPES.includes(file.type)) {
        toastMsg.error(SLIDE_IMAGE_INVALID_TYPE_MESSAGE);
        return;
      }

      uploadImage(file);
    };

    const openFileDialog = () => {
      if (isLoading) return;
      fileInputRef.current?.click();
    };

    const handleDelete = () => {
      // 업로드 중 삭제하면 onChange(null) 후 늦게 도착한 업로드 성공 콜백이 다시 onChange(slideImage)로 덮어써
      // 사용자가 삭제한 이미지가 되살아나는 race가 발생한다. 업로드 진행 중에는 삭제를 막는다.
      if (isLoading) return;
      onChange?.(null);
    };

    return (
      <UploadSlotStyle>
        {value ? (
          <div className="preview-container">
            <img src={value.url} alt="배너 이미지" className="preview-image" />
            <div className="toolbar">
              <button
                type="button"
                className="toolbar-btn"
                onClick={openFileDialog}
                disabled={isLoading}
                title="변경"
                aria-label="이미지 변경"
              >
                <EditOutlined rev={undefined} />
              </button>
              <button
                type="button"
                className="toolbar-btn delete"
                onClick={handleDelete}
                disabled={isLoading}
                title="삭제"
                aria-label="이미지 삭제"
              >
                <DeleteOutlined rev={undefined} />
              </button>
            </div>
          </div>
        ) : (
          <div
            className="empty-slot"
            onClick={openFileDialog}
            role="button"
            tabIndex={0}
            aria-label="이미지 업로드"
            aria-disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openFileDialog();
              }
            }}
          >
            {isLoading ? (
              <span className="loading-text">업로드 중...</span>
            ) : (
              <>
                <PlusOutlined rev={undefined} />
                <span className="upload-text">이미지 업로드</span>
              </>
            )}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={SLIDE_IMAGE_ALLOWED_MIME_TYPES.join(',')}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </UploadSlotStyle>
    );
  },
);

SlideImageUpload.displayName = 'SlideImageUpload';
